/**
 * Shared UI primitives.
 *
 * The legacy app had no component layer at all — every panel, modal, badge and
 * empty state was hand-written Tailwind inside a template literal, which is why
 * the same card appeared with four different corner radii. These are the shapes
 * that recur across the live screens, extracted once.
 */

import { forwardRef } from 'react';
import { resolveIcon } from './icons';
import { useScrollLock } from '../../hooks/useLocalStorage';
import { LoadingScreen, DEFAULT_LOADING_TITLE } from './LoadingScreen';

export { LoadingScreen, LoadingRings } from './LoadingScreen';
export { ParticleField } from './ParticleField';

/* ==========================================================================
   Icon — the lucide bridge

   The legacy markup referenced icons as kebab-case `data-lucide` strings and
   re-scanned the DOM after every render. Names are unchanged; they resolve
   through the explicit registry in ./icons.js (see the note there on why the
   registry is explicit rather than a namespace import).
   ========================================================================== */

export function Icon({ name, size = 18, className = '', ...rest }) {
  const Component = resolveIcon(name);
  return <Component size={size} className={className} aria-hidden="true" {...rest} />;
}

/* ==========================================================================
   Layout primitives
   ========================================================================== */

export function Panel({ children, className = '', padded = true, ...rest }) {
  return (
    <div
      className={`glass-panel rounded-2xl ${padded ? 'p-6' : ''} ${className}`}
      {...rest}
    >
      {children}
    </div>
  );
}

export function PageHeader({ title, subtitle, icon, actions }) {
  return (
    <div className="mb-6 flex flex-wrap items-center justify-between gap-4">
      <div className="flex items-center gap-3">
        {icon && (
          <span className="rounded-xl bg-cyan-500/10 p-2.5 text-cyan-400">
            <Icon name={icon} size={22} />
          </span>
        )}
        <div>
          <h1 className="text-xl font-bold text-white">{title}</h1>
          {subtitle && <p className="mt-0.5 text-sm text-slate-400">{subtitle}</p>}
        </div>
      </div>
      {actions && <div className="flex flex-wrap items-center gap-2">{actions}</div>}
    </div>
  );
}

export function SectionTitle({ children, icon, className = '' }) {
  return (
    <h3 className={`mb-4 flex items-center gap-2 text-lg font-bold text-white ${className}`}>
      {icon && <Icon name={icon} size={20} className="text-cyan-400" />}
      {children}
    </h3>
  );
}

/* ==========================================================================
   Buttons and inputs
   ========================================================================== */

const BUTTON_VARIANTS = {
  primary: 'btn-primary',
  ghost: 'btn-ghost',
  danger: 'btn-danger',
  success:
    'inline-flex items-center justify-center gap-2 rounded-xl bg-emerald-600 px-4 py-2.5 text-sm font-bold text-white transition-colors hover:bg-emerald-500 disabled:cursor-not-allowed disabled:bg-slate-700',
  subtle:
    'inline-flex items-center justify-center gap-2 rounded-xl bg-white/5 px-3 py-2 text-sm font-bold text-slate-300 transition-colors hover:bg-white/10 hover:text-white',
};

export const Button = forwardRef(function Button(
  { variant = 'primary', icon, loading = false, disabled, children, className = '', ...rest },
  ref,
) {
  return (
    <button
      ref={ref}
      type="button"
      disabled={disabled || loading}
      className={`${BUTTON_VARIANTS[variant] || BUTTON_VARIANTS.primary} ${className}`}
      {...rest}
    >
      {loading ? (
        <Icon name="loader-2" size={16} className="animate-spin" />
      ) : (
        icon && <Icon name={icon} size={16} />
      )}
      {children}
    </button>
  );
});

export function Field({ label, error, hint, children, required = false, className = '' }) {
  return (
    <div className={className}>
      {label && (
        <label className="field-label">
          {label}
          {required && <span className="text-red-400"> *</span>}
        </label>
      )}
      {children}
      {hint && !error && <p className="mt-1 text-[11px] text-slate-500">{hint}</p>}
      {error && (
        <p className="mt-1 flex items-center gap-1 text-[11px] font-bold text-red-400">
          <Icon name="alert-circle" size={12} />
          {error}
        </p>
      )}
    </div>
  );
}

export const Input = forwardRef(function Input({ className = '', ...rest }, ref) {
  return <input ref={ref} className={`field-input ${className}`} {...rest} />;
});

export const Textarea = forwardRef(function Textarea({ className = '', rows = 3, ...rest }, ref) {
  return <textarea ref={ref} rows={rows} className={`field-input resize-none ${className}`} {...rest} />;
});

export const Select = forwardRef(function Select({ className = '', children, ...rest }, ref) {
  return (
    <select ref={ref} className={`field-input ${className}`} {...rest}>
      {children}
    </select>
  );
});

export function Checkbox({ label, className = '', ...rest }) {
  return (
    <label className={`flex cursor-pointer items-center gap-2 text-sm text-slate-300 ${className}`}>
      <input
        type="checkbox"
        className="h-4 w-4 rounded border-slate-600 bg-slate-900 text-cyan-500 focus:ring-cyan-500"
        {...rest}
      />
      {label}
    </label>
  );
}

/* ==========================================================================
   Status display
   ========================================================================== */

export function Badge({ children, className = '', icon }) {
  return (
    <span className={`badge ${className}`}>
      {icon && <Icon name={icon} size={11} />}
      {children}
    </span>
  );
}

/**
 * The loading state, everywhere.
 *
 * This is the legacy loading view — particle field, three rotating rings,
 * gradient heading, hint pill — and `Spinner` renders it by default so every
 * existing call site shows it without being touched.
 *
 * `variant="inline"` drops the canvas and keeps the rings, for a loader inside
 * a card or modal where a starfield would overwhelm what it is covering.
 * `variant="cover"` fills the viewport, for boot and route changes.
 *
 * `label` becomes the heading; a call site that passes nothing gets the
 * legacy's own "جاري تحميل العيادة...".
 */
export function Spinner({ label, variant = 'full', hint, className = '' }) {
  return (
    <LoadingScreen
      title={label || DEFAULT_LOADING_TITLE}
      variant={variant}
      className={className}
      {...(hint === undefined ? {} : { hint })}
    />
  );
}

export function EmptyState({ icon = 'inbox', title, message, action }) {
  return (
    <div className="flex flex-col items-center justify-center py-12 text-center">
      <div className="mb-4 flex h-20 w-20 items-center justify-center rounded-full bg-slate-800/50">
        <Icon name={icon} size={36} className="text-slate-500" />
      </div>
      <h3 className="mb-1 text-lg font-bold text-white">{title}</h3>
      {message && <p className="max-w-sm text-sm text-slate-400">{message}</p>}
      {action && <div className="mt-4">{action}</div>}
    </div>
  );
}

/**
 * Failure panel.
 *
 * The legacy UI logged errors to the console and left the user staring at a
 * blank card. Surfacing the message and a retry is the minimum here.
 */
export function ErrorState({ error, onRetry, title = 'تعذر تحميل البيانات' }) {
  const message = typeof error === 'string' ? error : error?.message;
  return (
    <div className="flex flex-col items-center justify-center rounded-2xl border border-red-500/20 bg-red-500/5 py-10 text-center">
      <Icon name="alert-triangle" size={32} className="mb-3 text-red-400" />
      <h3 className="mb-1 text-base font-bold text-white">{title}</h3>
      {message && <p className="max-w-md px-4 text-sm text-slate-400" dir="ltr">{message}</p>}
      {onRetry && (
        <Button variant="ghost" icon="refresh-cw" className="mt-4" onClick={onRetry}>
          إعادة المحاولة
        </Button>
      )}
    </div>
  );
}

export function StatCard({ label, value, caption, icon, accent = 'cyan' }) {
  const accents = {
    cyan: { border: 'border-b-cyan-500', bg: 'bg-cyan-500/10', text: 'text-cyan-400' },
    amber: { border: 'border-b-amber-500', bg: 'bg-amber-500/10', text: 'text-amber-400' },
    emerald: { border: 'border-b-emerald-500', bg: 'bg-emerald-500/10', text: 'text-emerald-400' },
    purple: { border: 'border-b-purple-500', bg: 'bg-purple-500/10', text: 'text-purple-400' },
    red: { border: 'border-b-red-500', bg: 'bg-red-500/10', text: 'text-red-400' },
  };
  const tone = accents[accent] || accents.cyan;

  return (
    <div className={`glass-panel rounded-2xl border-b-4 p-5 transition-colors hover:bg-white/5 ${tone.border}`}>
      <div className="mb-4 flex items-start justify-between">
        <div className={`rounded-xl p-3 ${tone.bg} ${tone.text}`}>
          <Icon name={icon} size={22} />
        </div>
        {caption && <span className="font-mono text-xs text-slate-400">{caption}</span>}
      </div>
      <h3 className="mb-1 text-3xl font-bold text-white">{value}</h3>
      <p className="text-sm text-slate-400">{label}</p>
    </div>
  );
}

/* ==========================================================================
   Modal
   ========================================================================== */

const MODAL_SIZES = {
  sm: 'max-w-md',
  md: 'max-w-lg',
  lg: 'max-w-3xl',
  xl: 'max-w-5xl',
};

export function Modal({
  open,
  onClose,
  title,
  icon,
  size = 'md',
  children,
  footer,
  closeOnBackdrop = true,
}) {
  useScrollLock(open);
  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[200] flex items-center justify-center bg-black/80 p-4 backdrop-blur-sm"
      role="dialog"
      aria-modal="true"
      onMouseDown={(event) => {
        if (closeOnBackdrop && event.target === event.currentTarget) onClose?.();
      }}
    >
      <div
        className={`glass-panel relative flex max-h-[92vh] w-full flex-col overflow-hidden rounded-2xl ${MODAL_SIZES[size] || MODAL_SIZES.md}`}
      >
        <header className="flex shrink-0 items-center justify-between border-b border-white/10 bg-[#0f3460] p-4">
          <h3 className="flex items-center gap-2 text-lg font-bold text-white">
            {icon && <Icon name={icon} size={20} className="text-cyan-400" />}
            {title}
          </h3>
          <button
            type="button"
            onClick={onClose}
            className="text-slate-400 transition-colors hover:text-white"
            aria-label="إغلاق"
          >
            <Icon name="x" size={20} />
          </button>
        </header>

        <div className="custom-scrollbar flex-1 overflow-y-auto p-5">{children}</div>

        {footer && (
          <footer className="shrink-0 border-t border-white/10 bg-slate-900/60 p-4">{footer}</footer>
        )}
      </div>
    </div>
  );
}

/** Yes/no dialog — replaces the ~40 bare `confirm()` calls in the legacy code. */
export function ConfirmDialog({
  open,
  onClose,
  onConfirm,
  title = 'تأكيد الإجراء',
  message,
  confirmLabel = 'تأكيد',
  cancelLabel = 'إلغاء',
  destructive = false,
  loading = false,
}) {
  return (
    <Modal
      open={open}
      onClose={onClose}
      title={title}
      icon={destructive ? 'alert-triangle' : 'help-circle'}
      size="sm"
      footer={
        <div className="flex justify-end gap-2">
          <Button variant="ghost" onClick={onClose} disabled={loading}>
            {cancelLabel}
          </Button>
          <Button
            variant={destructive ? 'danger' : 'primary'}
            onClick={onConfirm}
            loading={loading}
          >
            {confirmLabel}
          </Button>
        </div>
      }
    >
      <p className="whitespace-pre-line text-sm leading-relaxed text-slate-300">{message}</p>
    </Modal>
  );
}

/* ==========================================================================
   Tabs
   ========================================================================== */

export function Tabs({ tabs, active, onChange, className = '' }) {
  return (
    <div className={`custom-scrollbar mb-5 flex gap-1 overflow-x-auto border-b border-white/10 ${className}`}>
      {tabs.map((tab) => {
        const isActive = tab.id === active;
        return (
          <button
            key={tab.id}
            type="button"
            onClick={() => onChange(tab.id)}
            aria-current={isActive ? 'page' : undefined}
            className={`flex shrink-0 items-center gap-2 border-b-2 px-4 py-2.5 text-sm font-bold transition-colors ${
              isActive
                ? 'border-cyan-500 text-cyan-300'
                : 'border-transparent text-slate-400 hover:text-slate-200'
            }`}
          >
            {tab.icon && <Icon name={tab.icon} size={16} />}
            {tab.label}
            {tab.count !== undefined && tab.count !== null && (
              <span className="rounded-full bg-white/10 px-1.5 py-0.5 text-[10px]">{tab.count}</span>
            )}
          </button>
        );
      })}
    </div>
  );
}

/* ==========================================================================
   Avatar
   ========================================================================== */

export function Avatar({ src, name = '', size = 40, className = '' }) {
  const initial = String(name).trim().charAt(0) || '؟';
  return (
    <div
      className={`flex shrink-0 items-center justify-center overflow-hidden rounded-full bg-slate-700 font-bold text-white ${className}`}
      style={{ width: size, height: size, fontSize: size * 0.4 }}
    >
      {src ? (
        <img src={src} alt={name} className="h-full w-full object-cover" />
      ) : (
        <span>{initial}</span>
      )}
    </div>
  );
}
